// SPDX-License-Identifier: LicenseRef-Proprietary

import React from 'react';
import { NodeProps } from './NodeProps';

export const TerminalNode: React.FC<NodeProps> = ({
  node,
  sizeConfig,
  isSelected,
  isHovered = false,
  isFocused = false,
  hasIssue = false,
  isRoot = false,
  onSelect,
  onHover,
  onFocus
}) => {
  const { width: w, height: h } = sizeConfig;
  const x = node.x + w / 2;
  const y = node.y + h / 2;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(node.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect(node.id);
    }
  };

  const handleMouseEnter = () => {
    onHover?.(node.id);
  };

  const handleMouseLeave = () => {
    onHover?.(null);
  };

  const handleFocus = () => {
    onFocus?.(node.id);
  };

  const handleBlur = () => {
    onFocus?.(null);
  };

  const fullLabel = node.label ?? '';
  
  // Calculate text padding (8px on each side, scaled with node size)
  // For triangles, use width for horizontal constraint
  const textPadding = Math.max(8, w * 0.1);
  const availableWidth = w - (textPadding * 2);
  
  // Estimate max characters based on font size and available width
  const charWidth = sizeConfig.fontSize * 0.6;
  const maxChars = Math.floor(availableWidth / charWidth);
  
  // Truncate label if needed
  const label = fullLabel.length > maxChars ? fullLabel.slice(0, maxChars - 1) + '…' : fullLabel;

  return (
    <g
      data-node
      data-id={node.id}
      data-clickable
      className={`node tree-node tree-node--terminal ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''} ${isFocused ? 'focused' : ''}`}
      data-selected={isSelected ? '' : undefined}
      tabIndex={0}
      role="graphics-symbol"
      aria-label={`Terminal node: ${fullLabel}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {/* Main shape */}
      <polygon
        className="shape"
        points={`${x - w/2},${y + h/2} ${x - w/2},${y - h/2} ${x + w/2},${y}`}
        fill="rgba(126, 140, 199, 0.25)"
        stroke={isSelected ? "#7e8cc7" : isRoot ? "rgba(126, 140, 199, 0.65)" : "rgba(126, 140, 199, 0.85)"}
        strokeWidth={isSelected ? 3 : isRoot ? 2 : 2.5}
        vectorEffect="non-scaling-stroke"
        style={{
          filter: isSelected 
            ? 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
            : isRoot
            ? 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))'
            : 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2))'
        }}
      />

      {/* Validation issue indicator */}
      {hasIssue && (
        <polygon
          points={`${x + w/2 - 12},${y - h/2} ${x + w/2},${y - h/2} ${x + w/2},${y - h/2 + 12}`}
          fill="var(--danger)"
        />
      )}

      {/* Text content (SVG-native, export-safe with halo) */}
      <text
        className="tree-node-text node-title"
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="rgba(255, 255, 255, 0.95)"
        fontSize={sizeConfig.fontSize}
        fontWeight="700"
        paintOrder="stroke"
        stroke="rgba(10,20,40,0.95)"
        strokeWidth={Math.max(2, sizeConfig.fontSize * 0.3)}
        strokeLinejoin="round"
      >
        {label}
      </text>
    </g>
  );
};

